import React, { useEffect, useId, useState } from 'react';
import { Alert, Modal } from 'react-bootstrap';
import Icon from '../common/Icons';
import { ClinicalButton } from './ui';
import './WorkAccidentConfirmModal.css';

function WaSegmentPair({
  formId,
  groupId,
  optionA,
  optionB,
  valueA,
  valueB,
  selected,
  onSelect,
  wide = false,
  disabled = false,
  variant = 'accent'
}) {
  const isB = selected === valueB;

  return (
    <div
      className={`consult-wa-segment consult-wa-segment--sub${
        variant === 'danger' ? ' consult-wa-segment--danger' : ''
      }${wide ? ' consult-wa-segment--wide' : ''}${isB ? ' is-option-b' : ''}${
        selected ? ' has-selection' : ''
      }`}
      role="radiogroup"
      aria-labelledby={groupId}
    >
      <span className="consult-wa-segment__glider" aria-hidden />

      <label className="consult-wa-segment__option">
        <input
          type="radio"
          className="consult-wa-segment__input"
          name={`${formId}-${groupId}`}
          value={valueA}
          checked={selected === valueA}
          disabled={disabled}
          onChange={() => onSelect(valueA)}
        />
        <span className="consult-wa-segment__pill">{optionA}</span>
      </label>

      <label className="consult-wa-segment__option">
        <input
          type="radio"
          className="consult-wa-segment__input"
          name={`${formId}-${groupId}`}
          value={valueB}
          checked={selected === valueB}
          disabled={disabled}
          onChange={() => onSelect(valueB)}
        />
        <span className="consult-wa-segment__pill">{optionB}</span>
      </label>
    </div>
  );
}

export default function WorkAccidentConfirmModal({
  show,
  onHide,
  onConfirm,
  copy = {},
  initialLocation = '',
  initialSeverity = '',
  disabled = false,
  tone = 'physician'
}) {
  const formId = useId();
  const [draftLocation, setDraftLocation] = useState('');
  const [draftSeverity, setDraftSeverity] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show) return;
    setDraftLocation(initialLocation || '');
    setDraftSeverity(initialSeverity || '');
    setError('');
  }, [show, initialLocation, initialSeverity]);

  const handleConfirm = () => {
    if (!draftLocation) {
      setError(copy.validationLocation || copy.VALIDATION_LOCATION || 'Indiquez le lieu.');
      return;
    }
    if (!draftSeverity) {
      setError(copy.validationSeverity || copy.VALIDATION_SEVERITY || 'Indiquez la gravité.');
      return;
    }
    onConfirm({ location: draftLocation, severity: draftSeverity });
  };

  const title = copy.modalTitle || copy.MODAL_TITLE || 'Accident du travail';
  const intro =
    copy.modalIntro ||
    copy.MODAL_INTRO ||
    'Confirmez la prise en charge AT et précisez le lieu et la gravité.';
  const confirmLabel = copy.modalConfirm || copy.MODAL_CONFIRM || "Confirmer l'AT";
  const cancelLabel = copy.modalCancel || copy.MODAL_CANCEL || 'Annuler';

  const toneClass =
    tone === 'headDoctor'
      ? 'consult-wa-modal--head-doctor'
      : tone === 'nurse'
        ? 'consult-wa-modal--nurse'
        : 'consult-wa-modal--physician';

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop="static"
      className={`consult-wa-modal ${toneClass}`}
      contentClassName="consult-wa-modal__content"
      aria-labelledby="consult-wa-modal-title"
    >
      <Modal.Header closeButton className="consult-wa-modal__head">
        <div className="consult-wa-modal__head-inner">
          <span className="consult-wa-modal__icon" aria-hidden>
            <Icon name="alertTriangle" size={22} />
          </span>
          <div>
            <Modal.Title id="consult-wa-modal-title" className="consult-wa-modal__title">
              {title}
            </Modal.Title>
            <p className="consult-wa-modal__intro mb-0">{intro}</p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="consult-wa-modal__body">
        {error ? (
          <Alert variant="danger" className="consult-wa-modal__alert py-2 small">
            {error}
          </Alert>
        ) : null}

        <div className="consult-wa-modal__field">
          <span className="consult-wa-modal__label" id={`${formId}-location`}>
            {copy.locationLabel || copy.LOCATION_LABEL || 'Lieu de l\'accident'}
          </span>
          <WaSegmentPair
            formId={formId}
            groupId="location"
            optionA={copy.locationWork || copy.LOCATION_WORK || 'Sur le travail'}
            optionB={copy.locationCommute || copy.LOCATION_COMMUTE || 'Sur le trajet'}
            valueA="WORK"
            valueB="COMMUTE"
            selected={draftLocation}
            onSelect={setDraftLocation}
            wide
            disabled={disabled}
          />
        </div>

        <div className="consult-wa-modal__field">
          <span className="consult-wa-modal__label" id={`${formId}-severity`}>
            {copy.severityLabel || copy.SEVERITY_LABEL || 'Gravité'}
          </span>
          <WaSegmentPair
            formId={formId}
            groupId="severity"
            optionA={copy.severityAtg || copy.SEVERITY_ATG || 'ATG'}
            optionB={copy.severityAtb || copy.SEVERITY_ATB || 'ATB'}
            valueA="ATG"
            valueB="ATB"
            selected={draftSeverity}
            onSelect={setDraftSeverity}
            variant="danger"
            disabled={disabled}
          />
        </div>
      </Modal.Body>

      <Modal.Footer className="consult-wa-modal__foot">
        <ClinicalButton variant="secondary" type="button" onClick={onHide} disabled={disabled}>
          {cancelLabel}
        </ClinicalButton>
        <ClinicalButton
          variant="danger"
          type="button"
          className="consult-wa-modal__confirm"
          onClick={handleConfirm}
          disabled={disabled}
        >
          {confirmLabel}
        </ClinicalButton>
      </Modal.Footer>
    </Modal>
  );
}
